<script setup>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { useAdminInfo } from '@/stores/useAdminInfo';
import DataTable from '@/components/common/DataTable.vue';
import formatDateTime from '@/utils/formatDateTime';
import dayjs from 'dayjs';
import {
    getAuditLogs,
    getAuditLogMeta,
    getAuditLogDetail,
    startAuditLogExport,
    getAuditLogExportStatus,
    downloadAuditLogExport
} from '@/api/audit';
import {
    buildAuditLogDiff,
    formatAuditRawJson,
    getAuditActionTagColor
} from '@/utils/logFormatter';
import {
    ClockCircleOutlined,
    UserOutlined,
    TagOutlined,
    CopyOutlined,
    DownOutlined,
    UpOutlined
} from '@ant-design/icons-vue';

const adminStore = useAdminInfo();

const canView = computed(() => adminStore.hasPermissionCode('200101'));
const canExport = computed(() => adminStore.hasPermissionCode('200102'));

const searchForm = reactive({
    module: undefined,
    action: undefined,
    keyword: '',
    created_range: []
});

const moduleOptions = ref([]);
const actionOptions = ref([]);
const tableData = ref([]);
const loading = ref(false);
const pagination = reactive({ total: 0, current: 1, pageSize: 20 });
const lastQueryRef = ref({});

const detailOpen = ref(false);
const detailLoading = ref(false);
const detailRecord = ref(null);
const exportSubmitting = ref(false);
const EXPORT_POLL_INTERVAL_MS = 5000;
const EXPORT_TASKS_STORAGE_KEY = 'audit_log_export_tasks';
const activeExportTasks = ref([]);
const exportPollTimers = ref({});
const exportTasksPanelExpanded = ref(true);

const loadExportTasksFromStorage = () => {
    try {
        const raw = localStorage.getItem(EXPORT_TASKS_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const saveExportTasksToStorage = () => {
    try {
        localStorage.setItem(
            EXPORT_TASKS_STORAGE_KEY,
            JSON.stringify(activeExportTasks.value)
        );
    } catch (e) {
        console.warn('儲存匯出任務到本地失敗', e);
    }
};

const getExportStatusText = (status) => {
    const map = {
        pending: '已排入佇列，等待執行',
        processing: '匯出中…',
        completed: '匯出完成，可下載',
        failed: '匯出失敗'
    };
    return map[status] || status || '未知';
};

const stopExportPolling = (exportId) => {
    const timerId = exportPollTimers.value[exportId];
    if (timerId != null) {
        clearInterval(timerId);
        delete exportPollTimers.value[exportId];
    }
};

const downloadExportFile = async (task) => {
    if (!task?.export_id || task.status !== 'completed') return;
    try {
        const blob = await downloadAuditLogExport(task.export_id);
        const url = window.URL.createObjectURL(
            new Blob([blob], { type: 'text/csv;charset=utf-8;' })
        );
        const link = document.createElement('a');
        link.href = url;
        link.download =
            task.original_filename ||
            `操作紀錄_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        dismissExportTask(task.export_id);
        message.success('下載已開始');
    } catch (err) {
        console.error('下載匯出檔失敗', err);
        showErrorMessage('下載失敗，請稍後再試');
    }
};

const pollExportStatus = (exportId) => {
    stopExportPolling(exportId);

    const doPoll = async () => {
        try {
            const res = await getAuditLogExportStatus(exportId);
            const data = res?.data || {};
            const task = activeExportTasks.value.find(
                (t) => t.export_id === exportId
            );
            if (!task) return;

            const prevStatus = task.status;
            task.status = data.status || task.status;
            task.row_count = data.row_count;
            task.original_filename = data.original_filename;
            task.error_message = data.error_message;
            task.finished_at = data.finished_at;
            saveExportTasksToStorage();

            if (task.status === 'completed' && prevStatus !== 'completed') {
                message.success(
                    `匯出 #${exportId} 完成（${task.row_count ?? 0} 筆），可下載 CSV`
                );
                stopExportPolling(exportId);
            }
            if (task.status === 'failed') {
                if (prevStatus !== 'failed') {
                    showErrorMessage(
                        task.error_message || `匯出 #${exportId} 失敗`
                    );
                }
                stopExportPolling(exportId);
            }
        } catch {
            // 輪詢失敗不中斷，下次再試
        }
    };

    doPoll();
    exportPollTimers.value[exportId] = setInterval(
        doPoll,
        EXPORT_POLL_INTERVAL_MS
    );
};

const addExportTask = (exportId) => {
    activeExportTasks.value.push({
        export_id: exportId,
        status: 'pending',
        row_count: null,
        original_filename: null,
        error_message: null
    });
    saveExportTasksToStorage();
    exportTasksPanelExpanded.value = true;
    pollExportStatus(exportId);
};

const dismissExportTask = (exportId) => {
    stopExportPolling(exportId);
    activeExportTasks.value = activeExportTasks.value.filter(
        (t) => t.export_id !== exportId
    );
    saveExportTasksToStorage();
};

const resumeExportPolling = () => {
    activeExportTasks.value.forEach((task) => {
        if (task.status === 'pending' || task.status === 'processing') {
            pollExportStatus(task.export_id);
        }
    });
};

const formatSubjectLine = (record) => {
    const parts = [];
    if (record?.subject_type_label) {
        parts.push(record.subject_type_label);
    }
    if (record?.subject_label) {
        parts.push(record.subject_label);
    } else if (record?.subject_type) {
        parts.push(record.subject_type);
    }
    if (
        record?.subject_id &&
        !String(record?.subject_label || '').includes(`#${record.subject_id}`)
    ) {
        parts.push(`#${record.subject_id}`);
    }
    return parts.length ? parts.join(' · ') : '—';
};

const tableScroll = { x: 1320 };

const columns = [
    {
        title: '時間',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 176,
        slots: { customRender: 'created_at' }
    },
    {
        title: '模組',
        dataIndex: 'module_label',
        key: 'module_label',
        width: 104,
        ellipsis: true
    },
    {
        title: '行為',
        dataIndex: 'action_label',
        key: 'action_label',
        width: 80,
        ellipsis: true
    },
    {
        title: '標的',
        key: 'subject',
        width: 320,
        ellipsis: { showTitle: false },
        slots: { customRender: 'subject' }
    },
    {
        title: '摘要',
        dataIndex: 'summary',
        key: 'summary',
        width: 420,
        ellipsis: { showTitle: false },
        slots: { customRender: 'summary' }
    },
    {
        title: '執行者',
        dataIndex: 'causer_name',
        key: 'causer_name',
        width: 112,
        ellipsis: true
    },
    {
        title: '操作',
        key: 'actions',
        width: 80,
        fixed: 'right',
        slots: { customRender: 'actions' }
    }
];

const normalizeSearchParams = (params = {}) => {
    const p = { ...params };
    if (Array.isArray(p.created_range) && p.created_range.length === 2) {
        const [start, end] = p.created_range;
        p.created_from = start ? dayjs(start).format('YYYY-MM-DD') : undefined;
        p.created_to = end ? dayjs(end).format('YYYY-MM-DD') : undefined;
    }
    delete p.created_range;
    return Object.fromEntries(
        Object.entries(p).filter(
            ([, v]) => v !== '' && v !== undefined && v !== null
        )
    );
};

const fetchLogs = async (extraParams = {}) => {
    if (!canView.value) return;
    try {
        loading.value = true;
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
            ...lastQueryRef.value,
            ...extraParams
        };
        const res = await getAuditLogs(params);
        if (res?.result) {
            tableData.value = res.data?.data || [];
            const paginationData = res.data?.pagination || {};
            pagination.total = paginationData.total || 0;
            pagination.current = paginationData.current_page || 1;
        } else {
            showErrorMessage(res?.message || '取得操作紀錄失敗');
            tableData.value = [];
            pagination.total = 0;
        }
    } catch (err) {
        console.error('GET /api/admin/audit-logs 失敗', err);
        tableData.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

const fetchMeta = async () => {
    const res = await getAuditLogMeta();
    if (res?.result) {
        moduleOptions.value = (res.data?.modules || []).map((m) => ({
            label: m.label,
            value: m.code
        }));
        actionOptions.value = (res.data?.actions || []).map((a) => ({
            label: a.label,
            value: a.code
        }));
    }
};

const handleSearch = async () => {
    pagination.current = 1;
    lastQueryRef.value = normalizeSearchParams(searchForm);
    await fetchLogs();
};

const handleReset = async () => {
    searchForm.module = undefined;
    searchForm.action = undefined;
    searchForm.keyword = '';
    searchForm.created_range = [];
    pagination.current = 1;
    lastQueryRef.value = {};
    await fetchLogs();
};

const handleTableChange = async ({ pagination: p }) => {
    pagination.current = p.current;
    pagination.pageSize = p.pageSize;
    await fetchLogs();
};

const openDetail = async (record) => {
    detailOpen.value = true;
    detailLoading.value = true;
    detailRecord.value = record;
    try {
        const res = await getAuditLogDetail(record.id);
        if (res?.result) {
            detailRecord.value = res.data;
        }
    } finally {
        detailLoading.value = false;
    }
};

const handleExport = async () => {
    if (!canExport.value) {
        message.warning('您沒有匯出操作紀錄的權限');
        return;
    }
    if (exportSubmitting.value) {
        return;
    }

    exportSubmitting.value = true;

    try {
        const params = normalizeSearchParams({
            ...searchForm,
            ...lastQueryRef.value
        });
        const startRes = await startAuditLogExport(params);
        const exportId = startRes?.data?.export_id;
        if (!exportId) {
            throw new Error('無法建立匯出任務');
        }

        addExportTask(exportId);
        message.info(
            '已建立匯出任務，請於右下角查看進度；完成後可下載，無需停留此頁'
        );
    } catch (err) {
        console.error('建立匯出任務失敗', err);
        showErrorMessage(err?.message || '建立匯出任務失敗');
    } finally {
        exportSubmitting.value = false;
    }
};

const detailDiffRows = computed(() => {
    if (!detailRecord.value) return [];
    return buildAuditLogDiff(
        detailRecord.value.old_values,
        detailRecord.value.new_values,
        detailRecord.value.old_values_display,
        detailRecord.value.new_values_display
    );
});

const hasDetailChanges = computed(() => detailDiffRows.value.length > 0);

const detailRawJson = computed(() => {
    if (!detailRecord.value) return null;
    return formatAuditRawJson(
        detailRecord.value.old_values,
        detailRecord.value.new_values
    );
});

const hasDetailRawJson = computed(() => Boolean(detailRawJson.value));

const copyRawJson = async () => {
    if (!detailRawJson.value) return;
    try {
        await navigator.clipboard.writeText(detailRawJson.value);
        message.success('已複製原始 JSON');
    } catch {
        message.error('複製失敗');
    }
};

const copyRequestId = async () => {
    const id = detailRecord.value?.request_id;
    if (!id) return;
    try {
        await navigator.clipboard.writeText(id);
        message.success('已複製 Request ID');
    } catch {
        message.error('複製失敗');
    }
};

onMounted(async () => {
    activeExportTasks.value = loadExportTasksFromStorage();
    resumeExportPolling();
    await fetchMeta();
    await fetchLogs();
});

onUnmounted(() => {
    Object.keys(exportPollTimers.value).forEach((exportId) =>
        stopExportPolling(Number(exportId))
    );
});
</script>

<template>
    <div class="page audit-page">
        <div class="page-header">
            <h1>操作紀錄</h1>
            <p>追蹤後台管理者的操作與資料異動</p>
        </div>

        <div class="content vaporwave-theme">
            <div class="toolbar">
                <div class="search-box filters-wrap">
                    <a-select
                        v-model:value="searchForm.module"
                        :options="moduleOptions"
                        allow-clear
                        placeholder="模組"
                        class="status-select audit-filter-module"
                        :dropdown-match-select-width="false"
                        :dropdown-style="{ minWidth: '168px' }"
                        size="middle"
                    />
                    <a-select
                        v-model:value="searchForm.action"
                        :options="actionOptions"
                        allow-clear
                        placeholder="行為"
                        class="status-select audit-filter-action"
                        :dropdown-match-select-width="false"
                        :dropdown-style="{ minWidth: '128px' }"
                        size="middle"
                    />
                    <a-input
                        v-model:value="searchForm.keyword"
                        allow-clear
                        placeholder="關鍵字（摘要、標的、執行者、欄位內容）"
                        class="status-select min-w-[260px]"
                        size="middle"
                        @press-enter="handleSearch"
                    />
                    <a-range-picker
                        v-model:value="searchForm.created_range"
                        :placeholder="['開始日期', '結束日期']"
                        class="status-select audit-filter-date"
                        size="middle"
                    />
                </div>
                <div class="filter-actions">
                    <a-button
                        size="middle"
                        class="unified-search-btn"
                        @click="handleSearch"
                    >
                        查詢
                    </a-button>
                    <a-button
                        size="middle"
                        class="unified-reset-btn"
                        @click="handleReset"
                    >
                        重置
                    </a-button>
                    <a-button
                        v-if="canExport"
                        size="middle"
                        type="primary"
                        :loading="exportSubmitting"
                        @click="handleExport"
                    >
                        匯出 CSV
                    </a-button>
                </div>
            </div>

            <DataTable
                title=""
                :columns="columns"
                :data-source="tableData"
                :pagination="pagination"
                :show-pagination="true"
                :scroll="tableScroll"
                :loading="loading"
                class="audit-table left-align-table"
                :no-card="true"
                @change="handleTableChange"
            >
                <template #created_at="{ text }">
                    <span class="audit-cell-nowrap">
                        {{ text ? formatDateTime(text) : '-' }}
                    </span>
                </template>
                <template #subject="{ record }">
                    <a-tooltip
                        :title="formatSubjectLine(record)"
                        placement="topLeft"
                    >
                        <span class="audit-cell-ellipsis audit-cell-subject">
                            {{ formatSubjectLine(record) }}
                        </span>
                    </a-tooltip>
                </template>
                <template #summary="{ text }">
                    <a-tooltip :title="text || '—'" placement="topLeft">
                        <span class="audit-cell-ellipsis">
                            {{ text || '—' }}
                        </span>
                    </a-tooltip>
                </template>
                <template #actions="{ record }">
                    <a-button
                        type="link"
                        size="small"
                        @click="openDetail(record)"
                    >
                        明細
                    </a-button>
                </template>
            </DataTable>
        </div>

        <a-drawer
            v-model:open="detailOpen"
            title="操作明細"
            width="720"
            class="audit-detail-drawer"
            :destroy-on-close="true"
        >
            <a-spin :spinning="detailLoading">
                <template v-if="detailRecord">
                    <div class="detail-hero">
                        <div class="detail-hero-tags">
                            <a-tag
                                :color="
                                    getAuditActionTagColor(detailRecord.action)
                                "
                            >
                                {{
                                    detailRecord.action_label ||
                                    detailRecord.action
                                }}
                            </a-tag>
                            <a-tag color="default">
                                {{
                                    detailRecord.module_label ||
                                    detailRecord.module
                                }}
                            </a-tag>
                        </div>
                        <div
                            v-if="detailRecord.subject_type_label"
                            class="detail-hero-subject-type"
                        >
                            {{ detailRecord.subject_type_label }}
                        </div>
                        <div class="detail-hero-title">
                            {{
                                detailRecord.subject_label ||
                                detailRecord.subject_type
                            }}
                            <span
                                v-if="detailRecord.subject_id"
                                class="detail-hero-id"
                            >
                                #{{ detailRecord.subject_id }}
                            </span>
                        </div>
                        <p
                            v-if="detailRecord.summary"
                            class="detail-hero-summary"
                        >
                            {{ detailRecord.summary }}
                        </p>
                    </div>

                    <div class="detail-meta-grid">
                        <div class="detail-meta-card">
                            <ClockCircleOutlined class="detail-meta-icon" />
                            <div>
                                <div class="detail-meta-label">時間</div>
                                <div class="detail-meta-value">
                                    {{
                                        detailRecord.created_at
                                            ? formatDateTime(
                                                  detailRecord.created_at
                                              )
                                            : '-'
                                    }}
                                </div>
                            </div>
                        </div>
                        <div class="detail-meta-card">
                            <UserOutlined class="detail-meta-icon" />
                            <div>
                                <div class="detail-meta-label">執行者</div>
                                <div class="detail-meta-value">
                                    {{ detailRecord.causer_name || '—' }}
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="detailRecord.request_id"
                            class="detail-meta-card detail-meta-card--wide"
                        >
                            <TagOutlined class="detail-meta-icon" />
                            <div class="detail-meta-request">
                                <div class="detail-meta-label">Request ID</div>
                                <div class="detail-meta-value detail-meta-mono">
                                    {{ detailRecord.request_id }}
                                </div>
                            </div>
                            <a-button
                                type="text"
                                size="small"
                                class="detail-copy-btn"
                                @click="copyRequestId"
                            >
                                <CopyOutlined />
                            </a-button>
                        </div>
                    </div>

                    <div class="detail-changes">
                        <div class="detail-section-title">資料異動</div>
                        <a-empty
                            v-if="!hasDetailChanges"
                            description="此筆操作無欄位異動紀錄"
                            class="detail-empty"
                        />
                        <div v-else class="detail-diff-list">
                            <div
                                v-for="row in detailDiffRows"
                                :key="row.key"
                                class="detail-diff-row"
                                :class="{
                                    'detail-diff-row--changed': row.changed
                                }"
                            >
                                <div class="detail-diff-label">
                                    {{ row.label }}
                                </div>
                                <div class="detail-diff-values">
                                    <template v-if="row.masked">
                                        <span class="detail-diff-masked">
                                            {{ row.newVal || row.oldVal }}
                                        </span>
                                    </template>
                                    <template
                                        v-else-if="
                                            detailRecord.action === 'created'
                                        "
                                    >
                                        <span class="detail-diff-new">
                                            {{ row.newVal }}
                                        </span>
                                    </template>
                                    <template
                                        v-else-if="
                                            detailRecord.action === 'deleted'
                                        "
                                    >
                                        <span class="detail-diff-old">
                                            {{ row.oldVal }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <span class="detail-diff-old">
                                            {{ row.oldVal }}
                                        </span>
                                        <span class="detail-diff-arrow">→</span>
                                        <span class="detail-diff-new">
                                            {{ row.newVal }}
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>

                    <a-collapse
                        v-if="hasDetailRawJson"
                        class="detail-raw-collapse"
                        :bordered="false"
                    >
                        <a-collapse-panel key="raw" header="原始資料（JSON）">
                            <div class="detail-raw-toolbar">
                                <a-button
                                    type="text"
                                    size="small"
                                    class="detail-copy-btn"
                                    @click="copyRawJson"
                                >
                                    <CopyOutlined />
                                    複製
                                </a-button>
                            </div>
                            <pre class="detail-raw-json">{{
                                detailRawJson
                            }}</pre>
                        </a-collapse-panel>
                    </a-collapse>
                </template>
            </a-spin>
        </a-drawer>

        <!-- 匯出任務常駐通知（右側）：背景處理，完成後手動下載 -->
        <div
            v-if="activeExportTasks.length > 0"
            class="export-tasks-panel"
            :class="{ 'is-expanded': exportTasksPanelExpanded }"
        >
            <div
                class="export-tasks-panel-header"
                @click="exportTasksPanelExpanded = !exportTasksPanelExpanded"
            >
                <span class="export-tasks-panel-title">
                    匯出進度 ({{ activeExportTasks.length }})
                </span>
                <DownOutlined
                    v-if="!exportTasksPanelExpanded"
                    class="export-tasks-chevron"
                />
                <UpOutlined v-else class="export-tasks-chevron" />
            </div>
            <div v-show="exportTasksPanelExpanded" class="export-tasks-list">
                <div
                    v-for="task in activeExportTasks"
                    :key="task.export_id"
                    class="export-task-card"
                    :class="{
                        'is-done':
                            task.status === 'completed' ||
                            task.status === 'failed'
                    }"
                >
                    <div class="export-task-title">
                        匯出 #{{ task.export_id }}
                    </div>
                    <div class="export-task-status">
                        {{ getExportStatusText(task.status) }}
                    </div>
                    <template v-if="task.status === 'completed'">
                        <div
                            v-if="task.row_count != null"
                            class="export-task-counts"
                        >
                            共 {{ task.row_count }} 筆
                        </div>
                        <div
                            class="export-task-actions export-task-actions--completed"
                        >
                            <a-button
                                type="primary"
                                size="small"
                                block
                                class="unified-add-btn export-task-btn"
                                @click.stop="downloadExportFile(task)"
                            >
                                下載 CSV
                            </a-button>
                            <a-button
                                type="link"
                                size="small"
                                class="export-task-skip-btn"
                                @click.stop="dismissExportTask(task.export_id)"
                            >
                                略過
                            </a-button>
                        </div>
                    </template>
                    <template v-else-if="task.status === 'failed'">
                        <div
                            v-if="task.error_message"
                            class="export-task-error"
                        >
                            {{ task.error_message }}
                        </div>
                        <div class="export-task-actions">
                            <a-button
                                type="primary"
                                size="small"
                                class="unified-add-btn export-task-btn"
                                @click.stop="dismissExportTask(task.export_id)"
                            >
                                關閉
                            </a-button>
                        </div>
                    </template>
                    <template v-else>
                        <div class="export-task-hint">
                            可繼續瀏覽或查詢，完成後會通知您
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.audit-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header h1 {
    margin-bottom: 4px;
}

.page-header p {
    color: #666;
    margin-bottom: 16px;
}

.toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 16px;
}

.filters-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
}

.toolbar :deep(.audit-filter-module.ant-select) {
    min-width: 168px;
    width: 168px;
}

.toolbar :deep(.audit-filter-action.ant-select) {
    min-width: 128px;
    width: 128px;
}

.toolbar :deep(.audit-filter-date.ant-picker) {
    min-width: 280px;
}

.toolbar :deep(.status-select),
.toolbar :deep(.status-select .ant-select-selector),
.toolbar :deep(.status-select.ant-picker) {
    height: 32px;
    font-size: 13px;
    border-radius: 8px;
    border: 1.5px solid rgba(200, 200, 210, 0.4);
    background: rgba(255, 255, 255, 0.95);
    color: #2a2a3a;
    transition: all 0.3s ease;
}

.toolbar :deep(.status-select:hover),
.toolbar :deep(.status-select.ant-select:hover .ant-select-selector),
.toolbar :deep(.status-select.ant-picker:hover) {
    border-color: rgba(42, 74, 122, 0.6);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2);
}

.toolbar :deep(.status-select.ant-select-focused .ant-select-selector),
.toolbar :deep(.status-select.ant-picker-focused) {
    border-color: rgba(42, 74, 122, 0.8);
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2);
}

.filter-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

.audit-table :deep(.ant-table) {
    table-layout: fixed;
}

.audit-table :deep(.ant-table-thead > tr > th),
.audit-table :deep(.ant-table-tbody > tr > td) {
    vertical-align: middle;
}

.audit-cell-nowrap {
    display: inline-block;
    white-space: nowrap;
}

.audit-cell-ellipsis {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.5;
}

.audit-cell-subject {
    color: #333;
}

.detail-hero {
    padding: 16px 18px;
    border-radius: 12px;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.08),
        rgba(125, 180, 255, 0.06)
    );
    border: 1px solid rgba(42, 74, 122, 0.15);
    margin-bottom: 16px;
}

.detail-hero-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
}

.detail-hero-subject-type {
    font-size: 12px;
    color: #9b7fd4;
    font-weight: 500;
    margin-bottom: 4px;
}

.detail-hero-title {
    font-size: 18px;
    font-weight: 600;
    color: #2a2a3a;
    line-height: 1.4;
}

.detail-hero-id {
    margin-left: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #888;
}

.detail-hero-summary {
    margin: 8px 0 0;
    font-size: 13px;
    color: #666;
    line-height: 1.5;
}

.detail-meta-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 20px;
}

.detail-meta-card {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
    background: #fafafa;
    border: 1px solid #eee;
}

.detail-meta-card--wide {
    grid-column: 1 / -1;
    align-items: center;
}

.detail-meta-icon {
    margin-top: 2px;
    color: #9b7fd4;
    font-size: 16px;
}

.detail-meta-label {
    font-size: 11px;
    color: #999;
    margin-bottom: 2px;
}

.detail-meta-value {
    font-size: 13px;
    color: #333;
    word-break: break-all;
}

.detail-meta-mono {
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
}

.detail-meta-request {
    flex: 1;
    min-width: 0;
}

.detail-copy-btn {
    color: #9b7fd4;
}

.detail-section-title {
    font-size: 14px;
    font-weight: 600;
    color: #444;
    margin-bottom: 10px;
    padding-left: 2px;
}

.detail-empty {
    margin: 24px 0;
}

.detail-diff-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-diff-row {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
}

.detail-diff-row--changed {
    background: rgba(42, 74, 122, 0.04);
    border-color: rgba(42, 74, 122, 0.18);
}

.detail-diff-label {
    font-size: 12px;
    color: #888;
    font-weight: 500;
    line-height: 1.6;
}

.detail-diff-values {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    line-height: 1.6;
}

.detail-diff-old {
    color: #cf6679;
    text-decoration: line-through;
    background: rgba(207, 102, 121, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
}

.detail-diff-arrow {
    color: #bbb;
    font-size: 12px;
}

.detail-diff-new {
    color: #2e7d5a;
    font-weight: 500;
    background: rgba(46, 125, 90, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
}

.detail-diff-masked {
    color: #5c6bc0;
    font-weight: 500;
    background: rgba(92, 107, 192, 0.08);
    padding: 1px 8px;
    border-radius: 4px;
}

.detail-raw-collapse {
    margin-top: 8px;
    background: transparent;
}

.detail-raw-collapse :deep(.ant-collapse-item) {
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
    background: #fafafa;
}

.detail-raw-collapse :deep(.ant-collapse-header) {
    font-size: 13px;
    font-weight: 500;
    color: #666;
    padding: 10px 14px !important;
}

.detail-raw-collapse :deep(.ant-collapse-content-box) {
    padding: 0 14px 14px !important;
}

.detail-raw-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 6px;
}

.detail-raw-json {
    margin: 0;
    padding: 12px;
    max-height: 320px;
    overflow: auto;
    font-size: 11px;
    line-height: 1.5;
    font-family: 'Courier New', Courier, monospace;
    color: #444;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 8px;
    white-space: pre-wrap;
    word-break: break-all;
}

:deep(.audit-detail-drawer .ant-drawer-body) {
    padding-top: 12px;
}

/* 匯出任務常駐通知（右側） */
.export-tasks-panel {
    position: fixed;
    top: 80px;
    right: 24px;
    width: 180px;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.15);
    z-index: 1050;
    font-family: 'Courier New', monospace;
    overflow: hidden;
    transition:
        width 0.2s ease,
        box-shadow 0.2s ease;
}

.export-tasks-panel.is-expanded {
    width: 300px;
    max-height: calc(100vh - 120px);
    box-shadow: 0 4px 24px rgba(42, 74, 122, 0.2);
    display: flex;
    flex-direction: column;
}

.export-tasks-panel-header {
    padding: 8px 12px;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.12),
        rgba(138, 175, 255, 0.08)
    );
    border-bottom: 1px solid transparent;
    font-weight: 600;
    font-size: 13px;
    color: #2a2a3a;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    user-select: none;
}

.export-tasks-panel.is-expanded .export-tasks-panel-header {
    padding: 10px 14px;
    border-bottom-color: rgba(42, 74, 122, 0.2);
}

.export-tasks-panel-header:hover {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.18),
        rgba(138, 175, 255, 0.12)
    );
}

.export-tasks-panel-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.export-tasks-chevron {
    flex-shrink: 0;
    font-size: 12px;
    color: rgba(42, 42, 58, 0.6);
}

.export-tasks-list {
    padding: 10px 12px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 280px;
}

.export-task-card {
    padding: 12px;
    background: rgba(248, 249, 250, 0.9);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
}

.export-task-card.is-done {
    border-color: rgba(200, 200, 210, 0.4);
}

.export-task-title {
    font-weight: 600;
    color: #2a2a3a;
    margin-bottom: 4px;
}

.export-task-status {
    font-size: 13px;
    color: rgba(42, 42, 58, 0.8);
    margin-bottom: 8px;
}

.export-task-hint {
    font-size: 12px;
    color: rgba(42, 42, 58, 0.55);
    line-height: 1.4;
}

.export-task-counts {
    font-size: 12px;
    color: rgba(42, 42, 58, 0.7);
    margin-bottom: 8px;
}

.export-task-error {
    font-size: 12px;
    color: #cf1322;
    margin-bottom: 8px;
    line-height: 1.4;
}

.export-task-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.export-task-actions--completed {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
}

.export-task-skip-btn {
    align-self: center;
    font-size: 12px;
    color: rgba(42, 42, 58, 0.55) !important;
    padding: 0 4px;
    height: auto;
}

.export-task-skip-btn:hover {
    color: #2a4a7a !important;
}

.export-task-btn {
    font-size: 12px;
}
</style>
